import { useCallback, useRef, useState } from 'react';

import type { FriendInfoJson } from '../platform/api';
import { useFriends } from './store';
import styles from './friends.module.css';

function FriendCard({ friend }: { friend: FriendInfoJson }): JSX.Element {
  const removeFriend = useFriends((s) => s.removeFriend);
  const block = useFriends((s) => s.blockUser);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
      setMenuOpen(false);
      document.removeEventListener('mousedown', handleClickOutside);
    }
  }, []);

  const toggleMenu = useCallback(() => {
    setMenuOpen((prev) => {
      if (!prev) {
        document.addEventListener('mousedown', handleClickOutside);
      } else {
        document.removeEventListener('mousedown', handleClickOutside);
      }
      return !prev;
    });
  }, [handleClickOutside]);

  return (
    <div className={styles.friendCard}>
      <div className={styles.friendInfo}>
        <span
          className={styles.onlineDot}
          style={{ background: friend.online ? '#57f287' : '#72767d' }}
        />
        <span className={styles.friendName}>
          {friend.display_name}
          <span className={styles.disc}>#{String(friend.discriminator).padStart(4, '0')}</span>
        </span>
      </div>
      <div className={styles.friendActions} ref={menuRef}>
        <button className={styles.menuBtn} onClick={toggleMenu}>
          &#x22EE;
        </button>
        {menuOpen && (
          <div className={styles.contextMenu}>
            <button
              className={styles.menuItem}
              onClick={() => { void removeFriend(friend.account_id); setMenuOpen(false); }}
            >
              Remove Friend
            </button>
            <button
              className={`${styles.menuItem} ${styles.menuItemDanger}`}
              onClick={() => { void block(friend.account_id); setMenuOpen(false); }}
            >
              Block
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export function FriendList(): JSX.Element {
  const friends = useFriends((s) => s.friends);

  if (friends.length === 0) {
    return (
      <div className={styles.emptyState}>
        No friends yet. Search for players above to add them!
      </div>
    );
  }

  return (
    <div className={styles.friendList}>
      {friends.map((f) => (
        <FriendCard key={f.account_id} friend={f} />
      ))}
    </div>
  );
}
